import useSearchParams from '@/hooks/useSearchParams';
import { AiOutlineClose } from 'react-icons/ai';
import { Filter, Sort } from '@/components/icons';
import useHandleOutside from '@/hooks/useHandleOutside';
import { FilterContainer } from '@/components/category/filters/filter-container';
import FilterItemWithCheckbox from '@/components/category/filters/filter-item-with-checkbox';
import { useSortOptions } from '@/store/sort.store';
import PriceFilter from '@/components/category/filters/priceFilter';
import { useMemo, useState, useCallback } from 'react';
import { sortKeys } from '@/constants';
import SkuFilter from '@/components/category/filters/sku-filter';

const FilterModal = ({
  type,
  setModalType,
  t,
  filters,
  ref,
  onFilter,
  handleSorting,
  searchIndexName,
  isFromSearch,
}) => {
  const { sortOptions, sortIndex } = useSortOptions();

  const { resetFilterQueryHandler, setFilterQueryHandler, getFilterObj } =
    useSearchParams();

  const sortData = isFromSearch ? sortOptions : sortKeys;

  const [selectedSort, setSelectedSort] = useState(
    sortData.find((opt) => opt.value === sortIndex)?.value ??
      `sortBy=${getFilterObj?.sortBy}&sortDir=${getFilterObj?.sortDir}` ??
      sortData[0]?.code
  );

  const handleSortChange = (value) => {
    isFromSearch
      ? handleSorting?.(value)
      : setFilterQueryHandler(
          Object.fromEntries(new URLSearchParams(value || ''))
        );

    setModalType(null);
  };
  useHandleOutside(ref, () => setModalType(null));

  const filterList = isFromSearch
    ? filters
        ?.filter((e) => e?.list?.length)
        .map((f, idx) => ({ ...f, key: idx }))
    : filters?.others?.map((f) => ({ ...f, key: f.code }));

  const options = useMemo(
    () =>
      isFromSearch
        ? sortOptions
        : [
            ...sortKeys.map((option) => ({
              value: option.code,
              label: t(option.text),
            })),
          ],
    [t, isFromSearch, sortOptions]
  );

  // store per-filter selected counts: { [filterKey]: number }
  const [selectedCounts, setSelectedCounts] = useState({});

  // helper to set selected count for a specific filter
  const setSelectedFor = useCallback((filterKey, count) => {
    setSelectedCounts((prev) => ({ ...prev, [filterKey]: count }));
  }, []);

  return (
    <div
      className={`fixed left-0 z-9999 flex ${type === 'sortBy' ? 'bottom-0 h-[55vh]' : ''} top-0 flex min-h-screen w-screen flex-col justify-end bg-black/60 shadow-2xl`}
    >
      <div
        ref={ref}
        className={`relative z-50 ${type === 'sortBy' ? 'h-[55vh]' : 'max-h-[70vh]'} w-screen overflow-y-auto rounded-t-3xl bg-white`}
      >
        <div className='mx-auto mt-3 h-1.5 w-12 rounded-full bg-gray-200' />
        <div className='bg-primary max-sm:border-b-border sticky top-0 z-50 flex items-center justify-between px-4 py-5 text-white max-md:max-h-9 max-md:min-h-9 max-sm:border-b max-sm:bg-white max-sm:text-black'>
          <h3 className='flex items-center gap-2 text-lg font-semibold max-sm:text-base'>
            {type === 'sortBy' ? (
              <Sort className='max-sm:hidden' />
            ) : (
              <Filter className='max-sm:hidden' />
            )}
            <span className='font-bold'>
              {t(type === 'sortBy' ? 'sorting_key' : 'filter_key')}
            </span>
          </h3>
          <AiOutlineClose
            onClick={() => setModalType(null)}
            size='22px'
            className='cursor-pointer max-sm:!size-4'
          />
        </div>

        {type === 'sortBy' ? (
          <div className='mt-4 space-y-2 px-1'>
            {options?.map((item) => (
              <label
                key={item?.value}
                className='border-border-50 flex items-center justify-between rounded-xl border bg-white px-3 py-3'
              >
                {console.log(item, selectedSort)}
                <span className='text-sm font-medium'>{item?.label}</span>
                <input
                  type='radio'
                  name='sort'
                  value={selectedSort}
                  checked={item?.value === selectedSort}
                  onChange={() => setSelectedSort(item?.value)}
                  className='size-4 accent-[var(--brand,#1e2b55)]'
                />
              </label>
            ))}
          </div>
        ) : (
          <div className='mb-4 flex flex-col gap-4 p-2 pb-20'>
            <PriceFilter
              removeTitle
              showInputs={false}
              searchIndexName={searchIndexName}
              mode={isFromSearch ? 'algolia' : 'category'}
            />
            <SkuFilter
              searchIndexName={searchIndexName}
              mode={isFromSearch ? 'algolia' : 'category'}
            />
            {filterList?.map((filter) => (
              <FilterContainer
                title={filter.title}
                key={filter.key}
                isFromSearch={isFromSearch}
                selected={selectedCounts[filter.key] ?? 0}
              >
                <FilterItemWithCheckbox
                  data={filter}
                  removeTitle
                  onFilter={isFromSearch ? onFilter : undefined}
                  parent={isFromSearch ? filter : undefined}
                  isFromSearch={isFromSearch}
                  t={t}
                  setSelected={(count) => setSelectedFor(filter.key, count)}
                  selected={selectedCounts[filter.key] ?? 0}
                />
              </FilterContainer>
            ))}
          </div>
        )}

        <div className='border-border-50 fixed inset-x-0 bottom-0 border-t bg-white p-4'>
          <div className='flex items-center gap-3'>
            <button
              className='flex-1 rounded-2xl border border-gray-300 py-3 font-semibold'
              onClick={() => {
                if (type === 'filter') {
                  resetFilterQueryHandler();
                  setModalType(null);
                } else {
                  setModalType(null);
                }
              }}
            >
              {t('Reset Filter')}
            </button>
            <button
              className='bg-primary flex-1 rounded-2xl py-3 font-semibold text-white shadow'
              onClick={() => {
                if (type === 'filter') {
                  setModalType(null);
                } else {
                  handleSortChange(selectedSort);
                }
              }}
            >
              {t('apply_key')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
export default FilterModal;
